import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';
import { ContactsService } from '@app/modules/portfolio/services/contacts.service';
import { ApiStatusCode } from '@common/enums/ApiStatusCode';
import { ConfirmationService } from 'primeng/api';
import { UserRole } from '@common/enums/UserRole';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { QuangularToasterService } from '@app/services/common/toaster.service';
import { isUserInRole } from '@app/services/common/utils';
import { CurrentUserStateService } from 'quangular';
import { REGEX } from '@common/constants/regex.constant';
@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnChanges  {

  @Input() showModal: boolean = false;
  @Output() showModalChange = new EventEmitter<boolean>();
  @Output() emitSavedChanges = new EventEmitter<any>();
  @Input() userData: any 
  @Input() mnemonic:  any;
  @Input() overflowRoleKey: any;

  // Sanitized photo URL for safe display
  safePhotoUrl: SafeUrl | null = null;
  isEditing = false;
  // Temporary copy of user data for editing
  editableUserData: any = {};
  // Backup original photo base64 (to revert on cancel)
  private originalPhotoBase64: string | null = null;
  private originalUserData: any = {};
  emailPattern: string = REGEX.EMAIL_PATTERN;
  searchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
	preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];

  showAddContactDialog: boolean = false;
  addContactObj: any;
  userInRole: boolean = false;
  isMobileInvalid: boolean = false;

  constructor(private sanitizer: DomSanitizer, private readonly contactsService: ContactsService,
    public readonly confirmationService: ConfirmationService,
    private readonly currentUserStateService: CurrentUserStateService,
    private readonly qToaster: QuangularToasterService
  ) { 
    const currentUser = this.currentUserStateService.value;
    if(currentUser){
      this.userInRole = isUserInRole(currentUser?.roles, UserRole.Admin);
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showModal) {
      this.showModal = changes.showModal.currentValue;
    }
    if (changes['userData']?.currentValue) {
        this.userData = changes['userData'].currentValue;
        // Store original copy
        this.originalUserData = structuredClone(this.userData);
        this.updateSafePhotoUrl(this.userData.photo);
    }
  }

  openModal(): void {
    this.showModal = true;
    this.showModalChange.emit(this.showModal);
  }

  closeModal(){
    this.isEditing = false;
    this.showModal = false;
    this.showModalChange.emit(this.showModal);
  }

    /**
   * Create a sanitized URL for the photo
   */
    private updateSafePhotoUrl(photoUrl): void {
      if (photoUrl) {
        this.safePhotoUrl = this.sanitizer.bypassSecurityTrustUrl('data:image/jpg;base64,'+photoUrl);
      } else {
        this.safePhotoUrl = null;
      }
    }

    /**
   * Get display name or a fallback from email
   */
    get displayName(): string {
      const data = this.isEditing ? this.editableUserData : this.userData;
      if (data?.displayName) {
        return data.displayName;
      }

      if (data?.firstName && data?.lastName) {
        return `${data.firstName} ${data.lastName}`;
      } 
      
      if (data?.firstName) {
        return data.firstName;
      } 
      
      if (data?.lastName) {
        return data.lastName;
      }
      
      if (data?.mail || data?.email) {
        const email = data?.mail || data?.email;
        return email.split('@')[0].replace('.', ' ');
      }
      
      return 'Unknown User';
    }

     /**
   * Open email client to send email to user
   */
  sendEmail(): void {
    if (this.userData?.mail || this.userData?.email) {
      const email = this.userData?.mail || this.userData?.email;
      const mailtoLink = `mailto:${email}`;
      window.open(mailtoLink, '_blank');
    }
  }
  
  /**
   * Open phone dialer to call user
   * @param useMobile Whether to use the mobile number (true) or business number (false)
   */
  callPhone(useMobile: boolean = false): void {
    const number = useMobile ? this.mobileNumber : this.phoneNumber;
    
    if (number) {
      // Create a phone number with only digits for the tel: protocol
      const cleanPhone = number.replace(REGEX.NON_DIGIT_GLOBAL, '');
      const telLink = `tel:${cleanPhone}`;
      window.open(telLink, '_blank');
    }
  }
  
  
  /**
   * Get job title or description
   */
  get jobTitle(): string {
    const data = this.isEditing ? this.editableUserData : this.userData;
    return data?.jobTitle || 
           data?.description || 
           'No title available';
  }
  
  /**
   * Get user's email address
   */
  get email(): string {
    const data = this.isEditing ? this.editableUserData : this.userData;
    return data?.mail ||  data?.email || '';
  }
  
  /**
   * Get user's department
   */
  get department(): string {
    const data = this.isEditing ? this.editableUserData : this.userData;
    return data?.company || 'No company information';
  }
  
  /**
   * Get user's business phone number
   */
  get phoneNumber(): string | null {
    const data = this.isEditing ? this.editableUserData : this.userData;
    if (data?.businessPhones && data.businessPhones.length > 0) {
      return data.businessPhones[0];
    }
    if (data?.businessPhone) {
      return data.businessPhone;
    }
    if (data?.phones && data.phones?.length > 0) {
      const businessPhone = data.phones.find((p: any) => p.type === 'business');
      if (businessPhone) return businessPhone.number;
    }
    return null;
  }
  
  /**
   * Get user's mobile phone number
   */
  get mobileNumber(): string | null {
    const data = this.isEditing ? this.editableUserData : this.userData;
    if (data?.mobilePhone) {
      return data.mobilePhone;
    }
    if (data?.phones && data.phones?.length > 0) {
      const mobilePhone = data.phones.find((p: any) => p.type === 'mobile');
      if (mobilePhone) return mobilePhone.number;
    }
    return null;
  }
  startEditing() {
    this.isEditing = true;
    // Deep copy userData into editableUserData to isolate edits
    this.editableUserData = structuredClone(this.userData || {});
    // Backup original photo base64 (if any)
    if (this.userData?.photo) {
      this.originalPhotoBase64 = this.userData.photo;
    } else {
      this.originalPhotoBase64 = null;
    }
  }
  cancelEditing() {
    this.isEditing = false;
    // Revert editableUserData and photo URL
    this.editableUserData = {};
    if (this.originalPhotoBase64) {
      this.updateSafePhotoUrl(this.originalPhotoBase64);
    } else {
      this.safePhotoUrl = null;
    }
  }
  private getDirtyFields(original: any, updated: any): any {
      const dirty: any = {};
      Object.keys(updated).forEach(key => {
        if (JSON.stringify(updated[key]) !== JSON.stringify(original[key])) {
          dirty[key] = updated[key];
        }
      });

      return dirty;
  }
  saveChanges(){
    // Set displayName
    this.editableUserData.displayName = `${this.editableUserData.firstName} ${this.editableUserData.lastName}`;
    this.editableUserData.name = this.editableUserData.displayName;
    // Get only changed fields
    const updatedFields = this.getDirtyFields(
      this.originalUserData,
      this.editableUserData
    );
    // Always include identifier
    updatedFields.sharePointId = this.originalUserData?.sharePointId;
    updatedFields.mnemonics = [this.mnemonic];
    if (updatedFields?.displayName) {
      delete updatedFields?.displayName;
    }

    // If nothing changed, do nothing
    if (Object.keys(updatedFields).length <= 2) {
        this.isEditing = false;
        return;
    }
    this.contactsService.updateContact(updatedFields).subscribe({
        next: (res) => {
          this.qToaster.showToast({
            message: res.message,
            status: res?.success
          });

          if (res?.success === ApiStatusCode.Success) {
            // Update local copies after successful save
            this.userData = { ...this.editableUserData };
            this.originalUserData = structuredClone(this.editableUserData);
            this.isEditing = false;
            this.showModal = false;
            this.emitSavedChanges.emit(this.originalUserData);
          }
        },
        error: (err) => {
          this.qToaster.showToast({
            message: err?.message || 'Update failed',
            status: ApiStatusCode.Failure
          });
          this.isEditing = false;
        }
    });
  }
  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const base64 = e.target.result.split(',')[1]; // Strip prefix
        // Update editable userData photo as base64 string (no prefix)
        this.editableUserData.photo = base64;
        // Update safe photo url for preview (with prefix)
        this.safePhotoUrl = this.sanitizer.bypassSecurityTrustUrl(e.target.result);
      };

      reader.readAsDataURL(file);
    }
  }
  get hasChanges(): boolean {
    if (!this.originalUserData || !this.editableUserData) return false;
    const dirty = this.getDirtyFields(this.originalUserData, this.editableUserData);
    return Object.keys(dirty).length > 0;
  }
  isFormValid(): boolean {
  if (!this.editableUserData) return false;

    const emailPattern = REGEX.EMAIL;
    const emailValid = !!this.editableUserData.email && emailPattern.test(this.editableUserData.email);
    return emailValid;
}

changeMobilePhone(event, model: any) {  
  this.editableUserData.mobilePhone = event?.nationalNumber || null;

  // Check valid number
  const errors = model?.control?.errors;
  if (!this.editableUserData.mobilePhone || !errors) {
    // No error → valid
    this.isMobileInvalid = false;
  } else if (errors?.validatePhoneNumber?.valid === false) {
    // Validation error → invalid
    this.isMobileInvalid = true;
  }
}
deleteContact(){ 
  this.confirmationService.confirm({
        message: `Do you want to go ahead and remove ${this.userData.displayName}'s Contact from ${this.userData.company ?? this.mnemonic} ?`,
        header: 'Remove Contact',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.contactsService.deleteContact({...this.userData, mnemonic: this.mnemonic}).subscribe({
          next: (res) => {
            this.qToaster.showToast({
              message: res.message,
              status: res?.status
            });

            if (res?.status == ApiStatusCode.Success) {
              this.isEditing = false;
              this.showModal = false;
              this.emitSavedChanges.emit(this.originalUserData);
            }
          },
          error: (err) => {
            this.qToaster.showToast({
              message: err.message,
              status: "error"
            });
            this.isEditing = false;
          }
          })
        },
      reject: () => {},
  });
    
}
  addContact(){
    this.addContactObj = {
      roleName: this.overflowRoleKey,
      mnemonic: this.mnemonic
    };
    this.showModal = false;
    this.showAddContactDialog = true;
  }

  addContactClose(event){
    this.addContactObj = null;
    this.showAddContactDialog = false;
    if (event) {
      this.qToaster.showToast({
        message: event?.message,
        status: event?.status
      });
      this.emitSavedChanges.emit(this.originalUserData);
    }
  }
}
